import { isChaosVariant, validateConfig, type GameConfig } from '../../shared/config';
import { ok, type Result } from '../../shared/result';
import { soloSetupFields, type GameMode } from '../../shared/modes';
import type { Phase } from '../../shared/protocol';

export interface ConfigPatchResult {
  /** The full validated configuration the patch produces. */
  config: GameConfig;
  /** The patch to send so another control's in-flight edit is not overwritten. */
  patch: Partial<GameConfig>;
}

/**
 * Apply a settings control's patch to a base configuration and validate the result.
 * The server merges patches the same way, so only the fields that changed are sent.
 */
export function applyConfigPatch(
  base: GameConfig,
  patch: Partial<GameConfig>,
): Result<ConfigPatchResult, string> {
  // Chaos is live-only; choosing Turns implies Classic rather than refusing the pace change.
  const resolved: Partial<GameConfig> =
    patch.pace === 'async' && isChaosVariant({ ...base, ...patch })
      ? { ...patch, variant: 'classic' }
      : patch;
  const validation = validateConfig({ ...base, ...resolved });
  if (!validation.ok) return validation;
  return ok({ config: validation.value, patch: resolved });
}

export type ConfigEditability = 'all' | 'setup' | 'none';

/**
 * Which settings the current player may change: a Versus host edits everything between
 * rounds, a solo player edits their mode's setup fields (`soloSetupFields`) before starting,
 * nobody else edits.
 */
export function configEditability(mode: GameMode, isHost: boolean, phase: Phase): ConfigEditability {
  if (!isHost || (phase !== 'lobby' && phase !== 'results')) return 'none';
  if (mode === 'versus') return 'all';
  return soloSetupFields(mode).length ? 'setup' : 'none';
}
